import { BigNumber, constants } from 'ethers';

import { WEB3_CONFIG } from '../lib';
import type { StarsMasterChef } from '../lib/contracts';
import { StarsMasterChefFactory } from '../lib/contracts/factories/StarsMasterChefFactory';
import {
  ethToWei,
  POOL_IDS,
  STARS_MASTER_CHEF_ADDRESS,
  weiToEth,
} from '../lib/smart-contract-utils';
import { useContract } from './contract.hooks';
import { useStarsEthLpTokenContract } from './lp-token-contract.hooks';
import { useStarsContract } from './stars-contract.hooks';

export const useStarsChefContract = (poolId: number) => {
  const starsContract = useStarsContract();
  const starsEthContract = useStarsEthLpTokenContract();
  const { contract: baseContract, ...walletInfo } =
    useContract<StarsMasterChef>(
      StarsMasterChefFactory,
      STARS_MASTER_CHEF_ADDRESS,
    );
  const contract = baseContract;
  const STARS_CHEF_NETWORK: string =
    WEB3_CONFIG.contracts[walletInfo.chain.key].starsChef;

  const getTotalStakedInPool = async () => {
    if (!contract) {
      return '0';
    }
    try {
      const poolInfoMantissa = await contract.poolInfo(poolId);
      return weiToEth(poolInfoMantissa.poolSupply).toString();
    } catch (e) {
      window.Sentry?.captureException(e, {
        tags: { file: 'stars-chef-contract', function: 'getTotalStakedInPool' },
        contexts: { data: { poolId } },
      });
      return '0';
    }
  };

  const getStakingAmountFromChef = async () => {
    if (!contract) {
      return '0';
    }
    try {
      const userInfoMantissa = await contract.userInfo(
        poolId,
        walletInfo.address ?? '',
      );
      return weiToEth(userInfoMantissa.amount);
    } catch (e) {
      window.Sentry?.captureException(e, {
        tags: {
          file: 'stars-chef-contract',
          function: 'getStakingAmountFromChef',
        },
        contexts: { data: { poolId } },
      });
      return '0';
    }
  };

  const getAPR = async (): Promise<string> => {
    if (!contract) {
      return '0';
    }

    try {
      const starsPerBlock = await contract.starsPerBlock();
      const totalAllocPoint = await contract.totalAllocPoint();

      return await contract.poolInfo(poolId).then(async poolInfo => {
        if (poolId === POOL_IDS[walletInfo.chain.type].STARS) {
          // star apr formula
          // starsPerBlock * blocks per year * alloc ratio / stake pool supply
          // alloc ratio => (allocPoint/totalAllocPoint)
          // BigNumber limitation - does not return decimals
          // multiply by 10000 to get percentage to 2 decimals
          // (100 (for percentage terms) * 100 (for decimals))
          const starsAPR = starsPerBlock
            .mul(10000)
            .mul(2102400)
            .mul(poolInfo.allocPoint)
            .div(totalAllocPoint)
            .div(poolInfo.poolSupply);
          // divide by 100 to get decimals
          const formattedAPR = parseFloat(starsAPR.toString()) / 100;
          return formattedAPR.toString();
        } else {
          const starsEthLpTokenSupply =
            await starsEthContract.getStarsEthTotalSupply();
          const starsEthReserves = await starsEthContract.getStarsEthReserves();
          const defaultBigNumber = BigNumber.from(0);

          const starsEthTotalSupply: BigNumber = !starsEthLpTokenSupply.eq(
            defaultBigNumber,
          )
            ? starsEthReserves[1]
                .mul(2)
                .mul(poolInfo.poolSupply)
                .div(starsEthLpTokenSupply)
            : defaultBigNumber;
          // stars_eth apr formula
          // starsPerBlock * blocks per year * alloc ratio / stars_eth total pool supply
          // alloc ratio => (allocPoint/totalAllocPoint)
          // where stars eth pool supply = stars reserve * 2 * stars eth staked in pool / total supply of lp tokens for stars eth
          const starsEthAPR =
            !totalAllocPoint.eq(defaultBigNumber) &&
            !starsEthTotalSupply.eq(defaultBigNumber)
              ? starsPerBlock
                  .mul(10000)
                  .mul(2102400)
                  .mul(poolInfo.allocPoint)
                  .div(totalAllocPoint)
                  .div(starsEthTotalSupply)
              : defaultBigNumber;

          // divide by 100 to get decimals
          const formattedAPR = parseFloat(starsEthAPR.toString()) / 100;
          return formattedAPR.toString();
        }
      });
    } catch (e) {
      window.Sentry?.captureException(e, {
        tags: {
          file: 'stars-chef-contract',
          function: 'getAPR',
        },
        contexts: { data: { poolId } },
      });
      return '0';
    }
  };

  const getRewardsAmount = async (): Promise<string> => {
    if (!contract) {
      return '0';
    }

    try {
      const pendingStarsMantissa = await contract.pendingStars(
        poolId,
        walletInfo.address ?? '',
      );

      return weiToEth(pendingStarsMantissa).toString();
    } catch (e) {
      window.Sentry?.captureException(e, {
        tags: { file: 'stars-chef-contract', function: 'getUnclaimedRewards' },
        contexts: { data: { poolId } },
      });

      return '0';
    }
  };

  const getWalletBalance = async (): Promise<string> => {
    if (!contract) {
      return '0';
    }
    try {
      if (poolId === POOL_IDS[walletInfo.chain.type].STARS) {
        const balance = await starsContract.getBalance(
          walletInfo.address ?? '',
        );

        return weiToEth(balance ?? BigNumber.from('0'));
      }
      if (poolId === POOL_IDS[walletInfo.chain.type].STARS_ETH) {
        const balance = await starsEthContract.getBalance(
          walletInfo.address ?? '',
        );

        return weiToEth(balance ?? BigNumber.from('0'));
      }
      return '0';
    } catch (e) {
      window.Sentry?.captureException(e, {
        tags: { file: 'stars-chef-contract', function: 'getWalletBalance' },
        contexts: { data: { poolId } },
      });

      return '0';
    }
  };

  const checkApproved = async (): Promise<boolean> => {
    if (!contract) {
      return false;
    }

    try {
      if (poolId === POOL_IDS[walletInfo.chain.type].STARS) {
        const allowance = await starsContract.allowance(
          walletInfo.address ?? '',
          STARS_CHEF_NETWORK,
        );

        return parseFloat(allowance) > 0;
      }
      if (poolId === POOL_IDS[walletInfo.chain.type].STARS_ETH) {
        const allowance = await starsEthContract.allowance(
          walletInfo.address ?? '',
          STARS_CHEF_NETWORK,
        );

        return parseFloat(allowance) > 0;
      }
    } catch (e) {
      window.Sentry?.captureException(e, {
        tags: { file: 'stars-chef-contract', function: 'checkApproved' },
        contexts: { data: { poolId } },
      });
    }
    return false;
  };

  const approveStarsChefContract = async (): Promise<boolean> => {
    try {
      if (poolId === POOL_IDS[walletInfo.chain.type].STARS) {
        await starsContract.approveStakingContract(
          STARS_CHEF_NETWORK,
          constants.MaxUint256,
        );
      }
      if (poolId === POOL_IDS[walletInfo.chain.type].STARS_ETH) {
        return await starsEthContract.approve(
          STARS_CHEF_NETWORK,
          constants.MaxUint256,
        );
      }
    } catch (e) {
      window.Sentry?.captureException(e, {
        tags: {
          file: 'stars-chef-contract',
          function: 'approveStarsChefContract',
        },
        contexts: { data: { poolId } },
      });
    }
    return false;
  };

  const depositToChef = async (amount: string): Promise<boolean> => {
    if (!contract) {
      return false;
    }
    try {
      const amountToStake = ethToWei(amount);
      const trx = await contract.deposit(poolId, amountToStake);
      await trx.wait();
      return true;
    } catch (e) {
      window.Sentry?.captureException(e, {
        tags: {
          file: 'stars-chef-contract',
          function: 'depositToChef',
        },
        contexts: { data: { poolId, amount } },
      });
      return false;
    }
  };

  const withdrawFromChef = async (amount: string): Promise<boolean> => {
    if (!contract) {
      return false;
    }
    try {
      const amountToUnStake = ethToWei(amount);
      const trx = await contract.withdraw(poolId, amountToUnStake);
      await trx.wait();
      return true;
    } catch (e) {
      window.Sentry?.captureException(e, {
        tags: {
          file: 'stars-chef-contract',
          function: 'withdrawFromChef',
        },
        contexts: { data: { poolId, amount } },
      });
      return false;
    }
  };

  const collectChefRewards = async (): Promise<boolean> => {
    if (!contract) {
      return false;
    }
    try {
      const trx = await contract.collectRewards(poolId);
      await trx.wait();
      return true;
    } catch (e) {
      window.Sentry?.captureException(e, {
        tags: {
          file: 'stars-chef-contract',
          function: 'collectChefRewards',
        },
        contexts: { data: { poolId } },
      });
      return false;
    }
  };

  return {
    walletInfo,
    initialized: contract !== undefined,
    getTotalStakedInPool,
    getAPR,
    getStakingAmountFromChef,
    getRewardsAmount,
    getWalletBalance,
    checkApproved,
    approveStarsChefContract,
    depositToChef,
    withdrawFromChef,
    collectChefRewards,
  };
};
